import { useState, useEffect } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Stack,
  Group,
  Button,
  Tabs,
  TextInput,
  ActionIcon,
  Modal,
  JsonInput,
  NumberInput,
  Divider,
  Alert,
  Loader,
  Badge,
  Grid,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { 
  IconSettings, 
  IconDeviceMobile, 
  IconRefresh, 
  IconCheck, 
  IconAlertCircle,
  IconDeviceFloppy,
  IconDatabase
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { 
  usePerformRequest, 
  PerformRequest 
} from "../../../hooks/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { HttpCodes } from "../../../lib/data";

interface Config {
  id: string;
  key: string;
  value: any;
  description: string;
  category: string;
  updatedAt: string;
}

const SettingsPage = () => {
  useEffect(() => {
    document.title = "Settings | Virel Admin";
  }, []);

  const [activeTab, setActiveTab] = useState<string | null>("mobile");
  const [isInitializing, setIsInitializing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [capInput, setCapInput] = useState<number | string>(10);
  const [capSaved, setCapSaved] = useState<number | null>(null);

  const { data, isLoading, reloadData } = usePerformRequest<{ data: Config[] }>({
    route: Endpoints.GetConfigs().route,
    method: "GET",
  });

  const configs = Array.isArray(data?.data) ? data.data : [];
  
  // Find specific configs
  const mobileVersionConfig = configs.find((c: Config) => c.key === "mobile_app_version");
  const leaderboardConfig = configs.find((c: Config) => c.key === "leaderboard_enabled");
  const inviteOnlyConfig = configs.find((c: Config) => c.key === "invite_only_mode");
  const referralCapConfig = configs.find((c: Config) => c.key === "referral_cap_per_person");

  useEffect(() => {
    if (referralCapConfig?.value != null && capSaved === null) {
      setCapInput(Number(referralCapConfig.value));
      setCapSaved(Number(referralCapConfig.value));
    }
  }, [referralCapConfig?.value, capSaved]);

  const mobileForm = useForm({
    initialValues: {
      iosLatest: "1.0.0",
      iosMin: "1.0.0",
      iosUrl: "",
      androidLatest: "1.0.0",
      androidMin: "1.0.0",
      androidUrl: "",
    },
  });

  useEffect(() => {
    if (mobileVersionConfig?.value) {
      const val = mobileVersionConfig.value;
      mobileForm.setValues({
        iosLatest: val.ios?.latestVersion || "1.0.0",
        iosMin: val.ios?.minVersion || "1.0.0",
        iosUrl: val.ios?.url || "",
        androidLatest: val.android?.latestVersion || "1.0.0",
        androidMin: val.android?.minVersion || "1.0.0",
        androidUrl: val.android?.url || "",
      });
    }
  }, [mobileVersionConfig]);

  const handleSaveMobile = async (values: typeof mobileForm.values) => {
    setIsSaving(true);
    try {
      const newValue = {
        ios: {
          latestVersion: values.iosLatest,
          minVersion: values.iosMin,
          url: values.iosUrl,
        },
        android: {
          latestVersion: values.androidLatest,
          minVersion: values.androidMin,
          url: values.androidUrl,
        },
      };

      const res = await PerformRequest({
        route: Endpoints.UpdateConfig("mobile_app_version").route,
        method: "PATCH",
        data: { value: newValue },
      });

      if (res.status === HttpCodes.Success) {
        notifications.show({
          title: "Success",
          message: "Mobile version configuration updated",
          color: "green",
          icon: <IconCheck size={16} />,
        });
        reloadData();
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to update configuration",
        color: "red",
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInitialize = async () => {
    setIsInitializing(true);
    try {
      const res = await PerformRequest({
        route: Endpoints.InitializeConfigs().route,
        method: "POST",
      });

      if (res.status === HttpCodes.Success) {
        notifications.show({
          title: "Initialized",
          message: "Default configurations have been reset",
          color: "blue",
          icon: <IconDatabase size={16} />,
        });
        reloadData();
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to initialize configurations",
        color: "red",
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsInitializing(false);
    }
  };

  return (
    <Container size="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={1}>System Settings</Title>
            <Text c="dimmed">Manage global application configurations and versioning</Text>
          </div>
          <Button 
            variant="light" 
            color="blue" 
            leftSection={<IconRefresh size={16} />}
            onClick={() => reloadData()}
            loading={isLoading}
          >
            Reload
          </Button>
        </Group>

        {!isLoading && configs.length === 0 && (
          <Alert icon={<IconAlertCircle size={16} />} title="No Configs Found" color="yellow">
            The system configuration database is empty. You may need to initialize it.
            <Button 
              variant="filled" 
              color="yellow" 
              mt="md" 
              size="xs"
              loading={isInitializing}
              onClick={handleInitialize}
            >
              Initialize Default Configs
            </Button>
          </Alert>
        )}

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="mobile" leftSection={<IconDeviceMobile size={16} />}>
              Mobile App Versioning
            </Tabs.Tab>
            <Tabs.Tab value="general" leftSection={<IconSettings size={16} />}>
              General Settings
            </Tabs.Tab>
            <Tabs.Tab value="advanced" leftSection={<IconDatabase size={16} />}>
              Maintenance
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="mobile" pt="xl">
            <form onSubmit={mobileForm.onSubmit(handleSaveMobile)}>
              <Stack gap="xl">
                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Paper shadow="sm" p="xl" radius="md" withBorder>
                      <Title order={3} mb="md" c="blue">iOS Deployment</Title>
                      <Stack>
                        <TextInput
                          label="Latest Version"
                          placeholder="e.g. 1.2.0"
                          description="The most recent version available on App Store"
                          {...mobileForm.getInputProps("iosLatest")}
                        />
                        <TextInput
                          label="Minimum Version"
                          placeholder="e.g. 1.0.0"
                          description="Versions below this will be FORCED to update"
                          {...mobileForm.getInputProps("iosMin")}
                        />
                        <TextInput
                          label="App Store URL"
                          placeholder="https://apps.apple.com/..."
                          {...mobileForm.getInputProps("iosUrl")}
                        />
                      </Stack>
                    </Paper>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Paper shadow="sm" p="xl" radius="md" withBorder>
                      <Title order={3} mb="md" c="green">Android Deployment</Title>
                      <Stack>
                        <TextInput
                          label="Latest Version"
                          placeholder="e.g. 1.2.0"
                          description="The most recent version available on Play Store"
                          {...mobileForm.getInputProps("androidLatest")}
                        />
                        <TextInput
                          label="Minimum Version"
                          placeholder="e.g. 1.0.0"
                          description="Versions below this will be FORCED to update"
                          {...mobileForm.getInputProps("androidMin")}
                        />
                        <TextInput
                          label="Play Store URL"
                          placeholder="https://play.google.com/..."
                          {...mobileForm.getInputProps("androidUrl")}
                        />
                      </Stack>
                    </Paper>
                  </Grid.Col>
                </Grid>

                <Paper p="md" withBorder radius="md" bg="blue.0">
                  <Group justify="space-between">
                    <Text size="sm" c="blue.9" fw={500}>
                      Updates are checked on every app launch. "Minimum Version" enforcement is immediate.
                    </Text>
                    <Button 
                      type="submit" 
                      color="blue" 
                      leftSection={<IconDeviceFloppy size={18} />}
                      loading={isSaving}
                    >
                      Save Version Config
                    </Button>
                  </Group>
                </Paper>
              </Stack>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="general" pt="xl">
            <Stack gap="md">
              {configs.filter((c: Config) => c.category === "general" && c.key !== "mobile_app_version").map((config: Config) => (
                <Paper key={config.id} p="md" withBorder radius="md">
                  <Group justify="space-between">
                    <Stack gap={4}>
                      <Text fw={700} tt="uppercase" size="xs" c="dimmed">{config.key}</Text>
                      <Text fw={600}>{config.description || "No description"}</Text>
                      <Text size="sm" c="dimmed">Current Value: <Badge variant="outline">{JSON.stringify(config.value)}</Badge></Text>
                    </Stack>
                    <Button variant="light" size="xs">Edit Value</Button>
                  </Group>
                </Paper>
              ))}
              
              <Divider label="Other Toggles" labelPosition="center" />
              
              <Paper p="md" withBorder radius="md">
                 <Group justify="space-between">
                    <Stack gap={4}>
                      <Text fw={600}>Leaderboard Visibility</Text>
                      <Text size="sm" c="dimmed">Enable or disable the global leaderboard in the mobile app</Text>
                    </Stack>
                    <Button 
                      variant="light" 
                      color={leaderboardConfig?.value ? "red" : "green"}
                      onClick={async () => {
                        const newVal = !leaderboardConfig?.value;
                        await PerformRequest({
                          route: Endpoints.UpdateConfig("leaderboard_enabled").route,
                          method: "PATCH",
                          data: { value: newVal }
                        });
                        reloadData();
                      }}
                    >
                      {leaderboardConfig?.value ? "Disable" : "Enable"}
                    </Button>
                 </Group>
              </Paper>

              <Paper p="md" withBorder radius="md">
                 <Group justify="space-between">
                    <Stack gap={4}>
                      <Text fw={600}>Invite-Only Mode {inviteOnlyConfig?.value ? "🔒 ON" : "OFF"}</Text>
                      <Text size="sm" c="dimmed">
                        When ON, new people can ONLY join with a valid referral code (waitlist auto-invite pauses).
                        Existing users, allowlisted people, admin-added accounts and the store-review account are always exempt.
                      </Text>
                    </Stack>
                    <Button
                      variant="light"
                      color={inviteOnlyConfig?.value ? "red" : "green"}
                      onClick={async () => {
                        const newVal = !inviteOnlyConfig?.value;
                        await PerformRequest({
                          route: Endpoints.UpdateConfig("invite_only_mode").route,
                          method: "PATCH",
                          data: { value: newVal }
                        });
                        reloadData();
                      }}
                    >
                      {inviteOnlyConfig?.value ? "Turn Off" : "Turn On"}
                    </Button>
                 </Group>
              </Paper>

              <Paper p="md" withBorder radius="md">
                 <Group justify="space-between" align="flex-end">
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Text fw={600}>Referrals Per Person</Text>
                      <Text size="sm" c="dimmed">
                        How many people one person can refer this period. When they hit it, their code stops
                        bringing new people in and stops earning. Raise it to re-open referrals. Set 0 for unlimited.
                      </Text>
                    </Stack>
                    <Group gap="sm" align="flex-end">
                      <NumberInput
                        value={capInput}
                        onChange={setCapInput}
                        min={0}
                        step={1}
                        w={100}
                        label="Cap"
                      />
                      <Button
                        variant="light"
                        color="green"
                        loading={isSaving}
                        disabled={Number(capInput) === capSaved}
                        onClick={async () => {
                          setIsSaving(true);
                          try {
                            await PerformRequest({
                              route: Endpoints.UpdateConfig("referral_cap_per_person").route,
                              method: "PATCH",
                              data: { value: Number(capInput) }
                            });
                            setCapSaved(Number(capInput));
                            reloadData();
                          } finally { setIsSaving(false); }
                        }}
                      >
                        Save
                      </Button>
                    </Group>
                 </Group>
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="advanced" pt="xl">
            <Paper shadow="xs" p="xl" radius="md" withBorder>
              <Stack gap="md">
                <Title order={3} c="red">Maintenance Actions</Title>
                <Text size="sm">These actions affect the entire platform configuration and should be used with caution.</Text>
                
                <Divider />
                
                <Group justify="space-between">
                  <div>
                    <Text fw={700}>Reset Configurations</Text>
                    <Text size="sm" c="dimmed">Initialize all configuration keys to their default factory settings.</Text>
                  </div>
                  <Button 
                    color="red" 
                    variant="outline" 
                    leftSection={<IconRefresh size={16} />}
                    loading={isInitializing}
                    onClick={handleInitialize}
                  >
                    Reset Defaults
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
};

export default SettingsPage;
